import React, { useState, useEffect } from 'react';
import { getAccounts, createAccount, updateAccount } from '../services/accounts';
import { getOperationTypes, getAccountConfig, toggleAccountOperation, setAccountOperations, getLLMModels, getAccountLLMConfig, updateAccountLLMConfig } from '../services/superadmin';
import { getAccountMCPConfig, updateAccountMCPConfig, testMCPConnection } from '../services/folder';
import './AccountsPage.css';

const EXCEL_FORMATS = [
  { value: '', label: 'Por defecto' },
  { value: 'juaristi', label: 'Juaristi (plantilla personalizada)' },
];

const AccountsPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    admin_email: '',
    admin_password: '',
    admin_first_name: '',
    admin_last_name: '',
    skip_machining_operations: false,
    excel_format: '',
    use_detection_pipeline: false,
    conical_detection_enabled: false,
    elements_search_enabled: false,
    orders_enabled: false,
  });
  const [selectedOperations, setSelectedOperations] = useState([]); // IDs de operaciones seleccionadas

  // Estado para MCP en formulario de creación
  const [createMcpEnabled, setCreateMcpEnabled] = useState(false);
  const [createMcpData, setCreateMcpData] = useState({
    mcp_api_url: '',
    mcp_api_key: '',
    base_path: '/',
    legacy_enabled: false,
    legacy_mcp_api_url: '',
    legacy_mcp_api_key: '',
    legacy_base_path: '/',
  });

  // Estado para configuración de cuenta seleccionada
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [allOperations, setAllOperations] = useState([]);
  const [accountConfig, setAccountConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);

  // Estado para configuración MCP
  const [mcpConfig, setMcpConfig] = useState(null);
  const [mcpFormData, setMcpFormData] = useState({
    enabled: false,
    mcp_api_url: '',
    mcp_api_key: '',
    base_path: '/',
    legacy_enabled: false,
    legacy_mcp_api_url: '',
    legacy_mcp_api_key: '',
    legacy_base_path: '/',
  });
  const [mcpSaving, setMcpSaving] = useState(false);
  const [mcpTesting, setMcpTesting] = useState(false);
  const [mcpTestResult, setMcpTestResult] = useState(null);

  // Estado para modelos LLM
  const [llmModels, setLlmModels] = useState([]);
  const [llmConfig, setLlmConfig] = useState(null);
  const [llmFormData, setLlmFormData] = useState({
    detection_llm_model_id: null,
    analysis_llm_model_id: null,
  });
  const [llmSaving, setLlmSaving] = useState(false);

  // Estado para skip_machining_operations
  const [skipMachiningUpdating, setSkipMachiningUpdating] = useState(false);

  // Estado para excel_format
  const [excelFormatUpdating, setExcelFormatUpdating] = useState(false);

  useEffect(() => {
    loadAccounts();
    loadOperations();
    loadLLMModels();
  }, []);

  const loadLLMModels = async () => {
    try {
      const models = await getLLMModels();
      setLlmModels(models);
    } catch (err) {
      console.error('Error al cargar modelos LLM:', err);
    }
  };

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const data = await getAccounts();
      setAccounts(data.accounts);
    } catch (err) {
      setError('Error al cargar cuentas');
    } finally {
      setLoading(false);
    }
  };

  const loadOperations = async () => {
    try {
      const operations = await getOperationTypes();
      setAllOperations(operations);
      // Por defecto todas las operaciones activas
      setSelectedOperations(operations.map(op => op.id));
    } catch (err) {
      console.error('Error al cargar operaciones:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Auto-generar slug desde el nombre
    if (name === 'name') {
      const slug = value
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
      setFormData((prev) => ({ ...prev, slug }));
    }
  };

  const handleOperationToggle = (operationId) => {
    setSelectedOperations((prev) =>
      prev.includes(operationId)
        ? prev.filter((id) => id !== operationId)
        : [...prev, operationId]
    );
  };

  const handleCreateMcpChange = (e) => {
    const { name, value } = e.target;
    setCreateMcpData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const newAccount = await createAccount(formData);

      // Configurar las operaciones seleccionadas para la nueva cuenta
      if (selectedOperations.length > 0 && newAccount.id) {
        await setAccountOperations(newAccount.id, selectedOperations, true);
      }

      // Configurar MCP si está habilitado
      if (createMcpEnabled && newAccount.id) {
        try {
          await updateAccountMCPConfig(newAccount.id, {
            enabled: true,
            mcp_api_url: createMcpData.mcp_api_url || null,
            mcp_api_key: createMcpData.mcp_api_key || null,
            base_path: createMcpData.base_path || '/',
            legacy_enabled: createMcpData.legacy_enabled || false,
            legacy_mcp_api_url: createMcpData.legacy_mcp_api_url || null,
            legacy_mcp_api_key: createMcpData.legacy_mcp_api_key || null,
            legacy_base_path: createMcpData.legacy_base_path || '/',
          });
        } catch (mcpErr) {
          console.error('Error configurando MCP:', mcpErr);
          // No fallar la creación por error de MCP
        }
      }

      setShowForm(false);
      setFormData({
        name: '',
        slug: '',
        admin_email: '',
        admin_password: '',
        admin_first_name: '',
        admin_last_name: '',
        skip_machining_operations: false,
        excel_format: '',
        use_detection_pipeline: false,
        conical_detection_enabled: false,
        elements_search_enabled: false,
        orders_enabled: false,
    orders_enabled: false,
      });
      // Reset estados
      setSelectedOperations(allOperations.map(op => op.id));
      setCreateMcpEnabled(false);
      setCreateMcpData({ mcp_api_url: '', mcp_api_key: '', base_path: '/' });
      loadAccounts();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear cuenta');
    } finally {
      setLoading(false);
    }
  };

  const handleAccountClick = async (account) => {
    if (selectedAccount?.id === account.id) {
      setSelectedAccount(null);
      setAccountConfig(null);
      setMcpConfig(null);
      setMcpTestResult(null);
      setLlmConfig(null);
      return;
    }

    setSelectedAccount(account);
    setConfigLoading(true);
    setMcpTestResult(null);

    try {
      const [config, mcp, llm] = await Promise.all([
        getAccountConfig(account.id),
        getAccountMCPConfig(account.id),
        getAccountLLMConfig(account.id).catch(() => null)
      ]);
      setAccountConfig(config);
      setMcpConfig(mcp);
      setMcpFormData({
        enabled: mcp.enabled || false,
        mcp_api_url: mcp.mcp_api_url || '',
        mcp_api_key: '',
        base_path: mcp.base_path || '/',
        legacy_enabled: mcp.legacy_enabled || false,
        legacy_mcp_api_url: mcp.legacy_mcp_api_url || '',
        legacy_mcp_api_key: '',
        legacy_base_path: mcp.legacy_base_path || '/',
      });
      setLlmConfig(llm);
      setLlmFormData({
        detection_llm_model_id: llm?.detection_llm_model_id || null,
        analysis_llm_model_id: llm?.analysis_llm_model_id || null,
      });
    } catch (err) {
      setError('Error al cargar configuración de la cuenta');
    } finally {
      setConfigLoading(false);
    }
  };

  const handleMcpInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setMcpFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSaveMcpConfig = async () => {
    if (!selectedAccount) return;
    setMcpSaving(true);
    setError('');

    try {
      const configToSave = {
        enabled: mcpFormData.enabled,
        mcp_api_url: mcpFormData.mcp_api_url || null,
        base_path: mcpFormData.base_path || '/',
        legacy_enabled: mcpFormData.legacy_enabled || false,
        legacy_mcp_api_url: mcpFormData.legacy_mcp_api_url || null,
        legacy_base_path: mcpFormData.legacy_base_path || '/',
      };
      // Only include API keys if they were changed
      if (mcpFormData.mcp_api_key) {
        configToSave.mcp_api_key = mcpFormData.mcp_api_key;
      }
      if (mcpFormData.legacy_mcp_api_key) {
        configToSave.legacy_mcp_api_key = mcpFormData.legacy_mcp_api_key;
      }

      const updatedConfig = await updateAccountMCPConfig(selectedAccount.id, configToSave);
      setMcpConfig(updatedConfig);
      setMcpFormData((prev) => ({ ...prev, mcp_api_key: '', legacy_mcp_api_key: '' }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar configuración MCP');
    } finally {
      setMcpSaving(false);
    }
  };

  const handleTestMcpConnection = async () => {
    if (!selectedAccount) return;
    setMcpTesting(true);
    setMcpTestResult(null);

    try {
      // First save the config, then test
      const configToSave = {
        enabled: mcpFormData.enabled,
        mcp_api_url: mcpFormData.mcp_api_url || null,
        base_path: mcpFormData.base_path || '/',
        legacy_enabled: mcpFormData.legacy_enabled || false,
        legacy_mcp_api_url: mcpFormData.legacy_mcp_api_url || null,
        legacy_base_path: mcpFormData.legacy_base_path || '/',
      };
      if (mcpFormData.mcp_api_key) {
        configToSave.mcp_api_key = mcpFormData.mcp_api_key;
      }
      if (mcpFormData.legacy_mcp_api_key) {
        configToSave.legacy_mcp_api_key = mcpFormData.legacy_mcp_api_key;
      }
      await updateAccountMCPConfig(selectedAccount.id, configToSave);
      setMcpFormData((prev) => ({ ...prev, mcp_api_key: '', legacy_mcp_api_key: '' }));

      // Now test the connection
      const result = await testMCPConnection(selectedAccount.id);
      setMcpTestResult(result);
      // Reload MCP config to get updated connection status
      const mcp = await getAccountMCPConfig(selectedAccount.id);
      setMcpConfig(mcp);
    } catch (err) {
      setMcpTestResult({ status: 'error', message: err.response?.data?.detail || 'Error de conexión' });
    } finally {
      setMcpTesting(false);
    }
  };

  const handleLlmSelectChange = (e) => {
    const { name, value } = e.target;
    setLlmFormData((prev) => ({
      ...prev,
      [name]: value ? parseInt(value) : null,
    }));
  };

  const handleSaveLlmConfig = async () => {
    if (!selectedAccount) return;
    setLlmSaving(true);
    setError('');

    try {
      const updatedConfig = await updateAccountLLMConfig(selectedAccount.id, {
        detection_llm_model_id: llmFormData.detection_llm_model_id,
        analysis_llm_model_id: llmFormData.analysis_llm_model_id,
      });
      setLlmConfig(updatedConfig);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar configuración LLM');
    } finally {
      setLlmSaving(false);
    }
  };

  const handleToggleOperation = async (operationId, currentEnabled) => {
    if (!selectedAccount) return;

    try {
      await toggleAccountOperation(selectedAccount.id, operationId, !currentEnabled);
      // Recargar configuración
      const config = await getAccountConfig(selectedAccount.id);
      setAccountConfig(config);
    } catch (err) {
      setError('Error al actualizar operación');
    }
  };

  const isOperationEnabled = (operationId) => {
    if (!accountConfig) return false;
    return accountConfig.enabled_operations?.some((op) => op.id === operationId) || false;
  };

  const [featureUpdating, setFeatureUpdating] = useState(null);

  const handleToggleFeature = async (field) => {
    if (!selectedAccount) return;
    setFeatureUpdating(field);
    setError('');
    try {
      const updatedAccount = await updateAccount(selectedAccount.id, {
        [field]: !selectedAccount[field],
      });
      setSelectedAccount(updatedAccount);
      setAccounts(prev => prev.map(acc =>
        acc.id === selectedAccount.id ? updatedAccount : acc
      ));
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al actualizar la funcionalidad');
    } finally {
      setFeatureUpdating(null);
    }
  };

  const handleToggleSkipMachining = async () => {
    if (!selectedAccount) return;
    setSkipMachiningUpdating(true);
    setError('');

    try {
      const newValue = !selectedAccount.skip_machining_operations;
      const updatedAccount = await updateAccount(selectedAccount.id, {
        skip_machining_operations: newValue,
      });
      setSelectedAccount(updatedAccount);
      setAccounts(prev => prev.map(acc =>
        acc.id === selectedAccount.id ? updatedAccount : acc
      ));
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al actualizar modo de análisis');
    } finally {
      setSkipMachiningUpdating(false);
    }
  };

  const handleUpdateExcelFormat = async (newFormat) => {
    if (!selectedAccount) return;
    setExcelFormatUpdating(true);
    setError('');

    try {
      const updatedAccount = await updateAccount(selectedAccount.id, {
        excel_format: newFormat || null,
      });
      setSelectedAccount(updatedAccount);
      setAccounts(prev => prev.map(acc =>
        acc.id === selectedAccount.id ? updatedAccount : acc
      ));
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al actualizar formato Excel');
    } finally {
      setExcelFormatUpdating(false);
    }
  };

  return (
    <div className="accounts-page">
      <div className="page-header">
        <h1>Gestión de Cuentas</h1>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary">
          {showForm ? 'Cancelar' : '+ Nueva Cuenta'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <div className="form-card">
          <h2>Crear Nueva Cuenta</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-section">
              <h3>Datos de la Cuenta</h3>
              <div className="form-group">
                <label>Nombre de la Cuenta *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Ej: Cliente ABC S.L."
                />
              </div>
              <div className="form-group">
                <label>Slug (identificador único) *</label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  required
                  placeholder="cliente-abc"
                />
              </div>
            </div>

            <div className="form-section">
              <h3>Primer Usuario Admin</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Nombre *</label>
                  <input
                    type="text"
                    name="admin_first_name"
                    value={formData.admin_first_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Apellidos *</label>
                  <input
                    type="text"
                    name="admin_last_name"
                    value={formData.admin_last_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="admin_email"
                  value={formData.admin_email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Contraseña *</label>
                <input
                  type="password"
                  name="admin_password"
                  value={formData.admin_password}
                  onChange={handleInputChange}
                  required
                  minLength={6}
                />
              </div>
            </div>

            <div className="form-section">
              <h3>Operaciones de Análisis</h3>
              <p className="form-hint">Selecciona qué operaciones tendrá activas esta cuenta</p>
              <div className="operations-checkboxes">
                {allOperations.map((operation) => (
                  <label key={operation.id} className="operation-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedOperations.includes(operation.id)}
                      onChange={() => handleOperationToggle(operation.id)}
                    />
                    <span className="checkbox-label">
                      <span className="operation-key-tag">{operation.key}</span>
                      {operation.name}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-section">
              <h3>Modo de Análisis</h3>
              <label className="mcp-enable-toggle" title="La cuenta solo detecta albaranes/pedidos y extrae sus datos (cliente, nº pedido, fechas, artículo). No analiza operaciones de mecanizado en los planos.">
                <input
                  type="checkbox"
                  checked={formData.skip_machining_operations}
                  onChange={(e) => setFormData(prev => ({ ...prev, skip_machining_operations: e.target.checked }))}
                />
                <span>Solo extraer metadatos de albarán (sin operaciones de mecanizado)</span>
              </label>
              <p className="form-hint">
                Activar para cuentas que solo necesitan detectar albaranes y extraer sus datos (cliente, proveedor, etc.),
                sin analizar operaciones de mecanizado en los planos técnicos.
              </p>
            </div>

            <div className="form-section">
              <h3>Funcionalidades de IA</h3>
              <label className="mcp-enable-toggle" title="El análisis localiza las cotas sobre el plano con un modelo YOLO y las lee con un modelo de visión. Alternativa avanzada al análisis clásico por prompts, con vista editable de detecciones.">
                <input
                  type="checkbox"
                  checked={formData.use_detection_pipeline}
                  onChange={(e) => setFormData(prev => ({ ...prev, use_detection_pipeline: e.target.checked }))}
                />
                <span>Pipeline de detecciones YOLO (cotas sobre el plano)</span>
              </label>
              <label className="mcp-enable-toggle" title="Un modelo de visión clasifica cada pieza como cónica o recta al analizarla, y la búsqueda de similares solo devuelve piezas del mismo tipo.">
                <input
                  type="checkbox"
                  checked={formData.conical_detection_enabled}
                  onChange={(e) => setFormData(prev => ({ ...prev, conical_detection_enabled: e.target.checked }))}
                />
                <span>Detección cónico/recto y filtro por tipo de pieza</span>
              </label>
              <label className="mcp-enable-toggle" title="Separa cada plano en zonas (vistas, cortes, detalles, cajetín) y permite buscar piezas parecidas por zonas concretas en orden de prioridad, con comparación asistida por IA.">
                <input
                  type="checkbox"
                  checked={formData.elements_search_enabled}
                  onChange={(e) => setFormData(prev => ({ ...prev, elements_search_enabled: e.target.checked }))}
                />
                <span>Búsqueda por elementos (zonas del plano)</span>
              </label>
              <label className="mcp-enable-toggle" title="Pedidos, ofertas y partes de trabajo: pestaña Pedidos, alta desde documento escaneado y enlace de cada pedido con su plano analizado.">
                <input
                  type="checkbox"
                  checked={formData.orders_enabled}
                  onChange={(e) => setFormData(prev => ({ ...prev, orders_enabled: e.target.checked }))}
                />
                <span>Pedidos y partes de trabajo</span>
              </label>
            </div>

            <div className="form-section">
              <h3>Formato de Excel</h3>
              <div className="form-group">
                <label>Plantilla de exportación</label>
                <select
                  name="excel_format"
                  value={formData.excel_format}
                  onChange={handleInputChange}
                >
                  {EXCEL_FORMATS.map(fmt => (
                    <option key={fmt.value} value={fmt.value}>{fmt.label}</option>
                  ))}
                </select>
                <p className="form-hint">
                  Selecciona el formato de Excel para exportar los análisis de esta cuenta.
                </p>
              </div>
            </div>

            <div className="form-section">
              <h3>Conexión a Carpeta Local (MCP)</h3>
              <p className="form-hint">Configura la conexión a una carpeta local del cliente mediante MCP</p>

              <label className="mcp-enable-toggle" title="Conecta la cuenta a una carpeta del servidor del cliente mediante un contenedor MCP, para escanear y analizar sus planos automáticamente.">
                <input
                  type="checkbox"
                  checked={createMcpEnabled}
                  onChange={(e) => setCreateMcpEnabled(e.target.checked)}
                />
                <span>Habilitar conexión a carpeta local</span>
              </label>

              {createMcpEnabled && (
                <div className="mcp-create-fields">
                  <div className="form-group">
                    <label>URL del API MCP</label>
                    <input
                      type="text"
                      name="mcp_api_url"
                      value={createMcpData.mcp_api_url}
                      onChange={handleCreateMcpChange}
                      placeholder="http://cliente.ddns.net:4400"
                    />
                  </div>

                  <div className="form-group">
                    <label>API Key</label>
                    <input
                      type="password"
                      name="mcp_api_key"
                      value={createMcpData.mcp_api_key}
                      onChange={handleCreateMcpChange}
                      placeholder="Introduce la API Key"
                    />
                  </div>

                  <div className="form-group">
                    <label>Ruta base</label>
                    <input
                      type="text"
                      name="base_path"
                      value={createMcpData.base_path}
                      onChange={handleCreateMcpChange}
                      placeholder="/"
                    />
                  </div>

                  <h4 style={{ marginTop: '1rem' }}>MCP legacy (solo lectura, opcional)</h4>
                  <label className="mcp-enable-toggle" title="Conexión de solo lectura a un MCP antiguo del cliente, para seguir localizando y descargando archivos analizados antes de la migración.">
                    <input
                      type="checkbox"
                      checked={createMcpData.legacy_enabled}
                      onChange={(e) => setCreateMcpData({ ...createMcpData, legacy_enabled: e.target.checked })}
                    />
                    <span>Habilitar MCP legacy</span>
                  </label>
                  {createMcpData.legacy_enabled && (
                    <>
                      <div className="form-group">
                        <label>URL del API MCP legacy</label>
                        <input
                          type="text"
                          name="legacy_mcp_api_url"
                          value={createMcpData.legacy_mcp_api_url}
                          onChange={handleCreateMcpChange}
                          placeholder="http://cliente.ddns.net:4401"
                        />
                      </div>
                      <div className="form-group">
                        <label>API Key legacy</label>
                        <input
                          type="password"
                          name="legacy_mcp_api_key"
                          value={createMcpData.legacy_mcp_api_key}
                          onChange={handleCreateMcpChange}
                          placeholder="Introduce la API Key del MCP legacy"
                        />
                      </div>
                      <div className="form-group">
                        <label>Ruta base legacy</label>
                        <input
                          type="text"
                          name="legacy_base_path"
                          value={createMcpData.legacy_base_path}
                          onChange={handleCreateMcpChange}
                          placeholder="/"
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="form-actions">
              <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">
                Cancelar
              </button>
              <button type="submit" disabled={loading} className="btn-primary">
                {loading ? 'Creando...' : 'Crear Cuenta'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de Cuentas */}
      <h2>Cuentas</h2>
      <div className="accounts-list">
        {loading && <p>Cargando...</p>}
        {accounts.map((account) => (
          <div
            key={account.id}
            className={`account-card ${selectedAccount?.id === account.id ? 'selected' : ''}`}
            onClick={() => handleAccountClick(account)}
          >
            <div className="account-card-header">
              <h3>{account.name}</h3>
              <span className="account-expand-icon">
                {selectedAccount?.id === account.id ? '−' : '+'}
              </span>
            </div>
            <p className="account-slug">Slug: {account.slug}</p>
            <p className="account-date">
              Creada: {new Date(account.created).toLocaleDateString('es-ES')}
            </p>

            {selectedAccount?.id === account.id && (
              <div className="account-config" onClick={(e) => e.stopPropagation()}>
                {/* Modo de Análisis */}
                <div className="analysis-mode-section">
                  <h4>Modo de Análisis</h4>
                  <label className="mcp-enable-toggle" title="La cuenta solo detecta albaranes/pedidos y extrae sus datos (cliente, nº pedido, fechas, artículo). No analiza operaciones de mecanizado en los planos.">
                    <input
                      type="checkbox"
                      checked={selectedAccount.skip_machining_operations || false}
                      onChange={handleToggleSkipMachining}
                      disabled={skipMachiningUpdating}
                    />
                    <span>Solo extraer metadatos de albarán (sin operaciones de mecanizado)</span>
                  </label>
                  {selectedAccount.skip_machining_operations && (
                    <p className="analysis-mode-hint">
                      Esta cuenta solo detectará albaranes y extraerá sus datos. No se analizarán operaciones de mecanizado.
                    </p>
                  )}
                </div>

                {/* Funcionalidades de IA */}
                <div className="analysis-mode-section">
                  <h4>Funcionalidades de IA</h4>
                  <label className="mcp-enable-toggle" title="El análisis localiza las cotas sobre el plano con un modelo YOLO y las lee con un modelo de visión. Alternativa avanzada al análisis clásico por prompts, con vista editable de detecciones.">
                    <input
                      type="checkbox"
                      checked={selectedAccount.use_detection_pipeline || false}
                      onChange={() => handleToggleFeature('use_detection_pipeline')}
                      disabled={featureUpdating === 'use_detection_pipeline'}
                    />
                    <span>Pipeline de detecciones YOLO (cotas sobre el plano)</span>
                  </label>
                  <label className="mcp-enable-toggle" title="Un modelo de visión clasifica cada pieza como cónica o recta al analizarla, y la búsqueda de similares solo devuelve piezas del mismo tipo.">
                    <input
                      type="checkbox"
                      checked={selectedAccount.conical_detection_enabled || false}
                      onChange={() => handleToggleFeature('conical_detection_enabled')}
                      disabled={featureUpdating === 'conical_detection_enabled'}
                    />
                    <span>Detección cónico/recto y filtro por tipo de pieza</span>
                  </label>
                  <label className="mcp-enable-toggle" title="Separa cada plano en zonas (vistas, cortes, detalles, cajetín) y permite buscar piezas parecidas por zonas concretas en orden de prioridad, con comparación asistida por IA.">
                    <input
                      type="checkbox"
                      checked={selectedAccount.elements_search_enabled || false}
                      onChange={() => handleToggleFeature('elements_search_enabled')}
                      disabled={featureUpdating === 'elements_search_enabled'}
                    />
                    <span>Búsqueda por elementos (zonas del plano)</span>
                  </label>
                  <label className="mcp-enable-toggle" title="Pedidos, ofertas y partes de trabajo: pestaña Pedidos, alta desde documento escaneado y enlace de cada pedido con su plano analizado.">
                    <input
                      type="checkbox"
                      checked={selectedAccount.orders_enabled || false}
                      onChange={() => handleToggleFeature('orders_enabled')}
                      disabled={featureUpdating === 'orders_enabled'}
                    />
                    <span>Pedidos y partes de trabajo</span>
                  </label>
                </div>

                {/* Formato de Excel */}
                <div className="excel-format-section">
                  <h4>Formato de Excel</h4>
                  <div className="excel-format-select">
                    <select
                      value={selectedAccount.excel_format || ''}
                      onChange={(e) => handleUpdateExcelFormat(e.target.value)}
                      disabled={excelFormatUpdating}
                    >
                      {EXCEL_FORMATS.map(fmt => (
                        <option key={fmt.value} value={fmt.value}>{fmt.label}</option>
                      ))}
                    </select>
                    {excelFormatUpdating && <span className="updating-indicator">Guardando...</span>}
                  </div>
                  <p className="excel-format-hint">
                    Formato de plantilla Excel para exportar análisis de esta cuenta.
                  </p>
                </div>

                <h4>Operaciones de Análisis Activas</h4>
                {configLoading ? (
                  <p className="config-loading">Cargando configuración...</p>
                ) : (
                  <div className="operations-list">
                    {allOperations.map((operation) => {
                      const enabled = isOperationEnabled(operation.id);
                      return (
                        <div key={operation.id} className="operation-item">
                          <label className="operation-toggle">
                            <input
                              type="checkbox"
                              checked={enabled}
                              onChange={() => handleToggleOperation(operation.id, enabled)}
                            />
                            <span className="toggle-slider"></span>
                          </label>
                          <div className="operation-info">
                            <span className="operation-key-badge">{operation.key}</span>
                            <span className="operation-name">{operation.name}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* MCP Folder Configuration */}
                <div className="mcp-config-section">
                  <h4>Conexión a Carpeta Local (MCP)</h4>
                  {configLoading ? (
                    <p className="config-loading">Cargando...</p>
                  ) : (
                    <>
                      <div className="mcp-status">
                        <span className={`mcp-status-badge ${mcpConfig?.connection_status || 'unknown'}`}>
                          {mcpConfig?.connection_status === 'healthy' ? 'Conectado' :
                           mcpConfig?.connection_status === 'error' ? 'Error' : 'Sin configurar'}
                        </span>
                        {mcpConfig?.last_connection_test && (
                          <span className="mcp-last-test">
                            Último test: {new Date(mcpConfig.last_connection_test).toLocaleString('es-ES')}
                          </span>
                        )}
                      </div>

                      <div className="mcp-form">
                        <label className="mcp-enable-toggle">
                          <input
                            type="checkbox"
                            name="enabled"
                            checked={mcpFormData.enabled}
                            onChange={handleMcpInputChange}
                          />
                          <span>Habilitar conexión a carpeta</span>
                        </label>

                        {mcpFormData.enabled && (
                          <>
                            <div className="mcp-form-group">
                              <label>URL del API MCP</label>
                              <input
                                type="text"
                                name="mcp_api_url"
                                value={mcpFormData.mcp_api_url}
                                onChange={handleMcpInputChange}
                                placeholder="http://cliente.ddns.net:4400"
                              />
                            </div>

                            <div className="mcp-form-group">
                              <label>API Key {mcpConfig?.mcp_api_url && '(dejar vacío para mantener)'}</label>
                              <input
                                type="password"
                                name="mcp_api_key"
                                value={mcpFormData.mcp_api_key}
                                onChange={handleMcpInputChange}
                                placeholder={mcpConfig?.mcp_api_url ? '••••••••' : 'Introduce la API Key'}
                              />
                            </div>

                            <div className="mcp-form-group">
                              <label>Ruta base</label>
                              <input
                                type="text"
                                name="base_path"
                                value={mcpFormData.base_path}
                                onChange={handleMcpInputChange}
                                placeholder="/"
                              />
                            </div>

                            <h4 style={{ marginTop: '1rem' }}>MCP legacy (solo lectura, opcional)</h4>
                            <label className="mcp-enable-toggle" title="Conexión de solo lectura a un MCP antiguo del cliente, para seguir localizando y descargando archivos analizados antes de la migración.">
                              <input
                                type="checkbox"
                                name="legacy_enabled"
                                checked={mcpFormData.legacy_enabled}
                                onChange={handleMcpInputChange}
                              />
                              <span>Habilitar MCP legacy</span>
                            </label>
                            {mcpFormData.legacy_enabled && (
                              <>
                                <div className="mcp-form-group">
                                  <label>URL del API MCP legacy</label>
                                  <input
                                    type="text"
                                    name="legacy_mcp_api_url"
                                    value={mcpFormData.legacy_mcp_api_url}
                                    onChange={handleMcpInputChange}
                                    placeholder="http://cliente.ddns.net:4401"
                                  />
                                </div>
                                <div className="mcp-form-group">
                                  <label>API Key legacy {mcpConfig?.legacy_mcp_api_url && '(dejar vacío para mantener)'}</label>
                                  <input
                                    type="password"
                                    name="legacy_mcp_api_key"
                                    value={mcpFormData.legacy_mcp_api_key}
                                    onChange={handleMcpInputChange}
                                    placeholder={mcpConfig?.legacy_mcp_api_url ? '••••••••' : 'Introduce la API Key legacy'}
                                  />
                                </div>
                                <div className="mcp-form-group">
                                  <label>Ruta base legacy</label>
                                  <input
                                    type="text"
                                    name="legacy_base_path"
                                    value={mcpFormData.legacy_base_path}
                                    onChange={handleMcpInputChange}
                                    placeholder="/"
                                  />
                                </div>
                              </>
                            )}
                          </>
                        )}

                        <div className="mcp-actions">
                          <button
                            onClick={handleSaveMcpConfig}
                            disabled={mcpSaving}
                            className="btn-primary btn-small"
                          >
                            {mcpSaving ? 'Guardando...' : 'Guardar'}
                          </button>
                          {mcpFormData.enabled && mcpFormData.mcp_api_url && (
                            <button
                              onClick={handleTestMcpConnection}
                              disabled={mcpTesting}
                              className="btn-secondary btn-small"
                            >
                              {mcpTesting ? 'Probando...' : 'Probar conexión'}
                            </button>
                          )}
                        </div>

                        {mcpTestResult && (
                          <div className={`mcp-test-result ${mcpTestResult.status}`}>
                            {mcpTestResult.status === 'healthy' ? '✓ ' : '✗ '}
                            {mcpTestResult.message}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* LLM Models Configuration */}
                <div className="llm-config-section">
                  <h4>Modelos de IA</h4>
                  {configLoading ? (
                    <p className="config-loading">Cargando...</p>
                  ) : (
                    <div className="llm-form">
                      <div className="llm-form-group">
                        <label>Modelo de Detección</label>
                        <span className="llm-hint">Para detectar tipo de página (albarán/plano)</span>
                        <select
                          name="detection_llm_model_id"
                          value={llmFormData.detection_llm_model_id || ''}
                          onChange={handleLlmSelectChange}
                        >
                          <option value="">-- Usar por defecto --</option>
                          {llmModels.map((model) => (
                            <option key={model.id} value={model.id}>
                              {model.name} ({model.provider})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="llm-form-group">
                        <label>Modelo de Análisis</label>
                        <span className="llm-hint">Para analizar operaciones de mecanizado</span>
                        <select
                          name="analysis_llm_model_id"
                          value={llmFormData.analysis_llm_model_id || ''}
                          onChange={handleLlmSelectChange}
                        >
                          <option value="">-- Usar por defecto --</option>
                          {llmModels.map((model) => (
                            <option key={model.id} value={model.id}>
                              {model.name} ({model.provider})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="llm-actions">
                        <button
                          onClick={handleSaveLlmConfig}
                          disabled={llmSaving}
                          className="btn-primary btn-small"
                        >
                          {llmSaving ? 'Guardando...' : 'Guardar'}
                        </button>
                      </div>

                      {llmConfig && (
                        <div className="llm-current-config">
                          <span className="llm-config-label">Actual:</span>
                          <span>Detección: {llmConfig.detection_model_name || 'Por defecto'}</span>
                          <span>Análisis: {llmConfig.analysis_model_name || 'Por defecto'}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AccountsPage;
